import { config } from '@/workspace/lib/antd'
import { queryConfig } from '@/workspace/lib/react-query'
import Bugsnag, { type Event } from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ConfigProvider } from 'antd'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { type ReactNode, useState } from 'react'
import React from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import NormalizeCss from '../components/normalize-css'
import configureStore from '../v2/Store/configureStore'
import { initializeBugsnag } from './bugsnag-config'

type AppProviderProps = {
  children: ReactNode
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const bugSnagApiKey = process.env.BUGSNAG_KEY
  const appVersion = process.env.RELEASE

  if (bugSnagApiKey) {
    initializeBugsnag(bugSnagApiKey, appVersion)
  }

  const BugsnagErrorBoundary = bugSnagApiKey
    ? (Bugsnag.getPlugin('react')?.createErrorBoundary(React) ?? React.Fragment)
    : ({ children }: { children: JSX.Element }) => {
        return <>{children}</>
      }

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
      }),
  )
  const { store, persistor } = configureStore()

  return (
    <BugsnagErrorBoundary>
      <NuqsAdapter>
        <HelmetProvider>
          <QueryClientProvider client={queryClient}>
            {/* for v2 start */}
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                {/* for v2  end */}
                <ConfigProvider {...config}>
                  <NormalizeCss />
                  <ReactQueryDevtools />
                  {children}
                </ConfigProvider>
              </PersistGate>
            </Provider>
          </QueryClientProvider>
        </HelmetProvider>
      </NuqsAdapter>
    </BugsnagErrorBoundary>
  )
}
